import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

/** Fraction of nodes to keep on mobile (0.5 = half) */
const MOBILE_NODES_RATIO = 0.5;

export type ConnectorsAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

type NodeParticle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  phase: number;
};

const MAX_DIST = 120;

/** Drifting nodes with proximity lines — Delightful-er Forms spec. */
export const ConnectorsAnimation: FC<ConnectorsAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function tc(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 78%, ${alpha * 0.72})`;
        case "light":
          return `hsla(${hue}, 48%, 52%, ${alpha * 0.65})`;
        case "darkdull":
          return `hsla(${hue}, 22%, 68%, ${alpha * 0.52})`;
        case "lightdull":
          return `hsla(${hue}, 12%, 52%, ${alpha * 0.5})`;
        default:
          return `hsla(${hue}, 68%, 78%, ${alpha * 0.72})`;
      }
    }

    function tg(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 80%, 88%, ${alpha * 0.2})`;
        case "light":
          return `hsla(${hue}, 55%, 78%, ${alpha * 0.28})`;
        case "darkdull":
          return `hsla(${hue}, 28%, 75%, ${alpha * 0.15})`;
        case "lightdull":
          return `hsla(${hue}, 18%, 82%, ${alpha * 0.22})`;
        default:
          return `hsla(${hue}, 80%, 88%, ${alpha * 0.2})`;
      }
    }

    function gradEnd(): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 80%, 88%, 0)`;
        case "light":
          return `hsla(${hue}, 55%, 78%, 0)`;
        case "darkdull":
          return `hsla(${hue}, 28%, 75%, 0)`;
        case "lightdull":
          return `hsla(${hue}, 18%, 82%, 0)`;
        default:
          return `hsla(${hue}, 80%, 88%, 0)`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    const nodeCount = isMobile ? Math.round(65 * MOBILE_NODES_RATIO) : 65;
    const nodes: NodeParticle[] = Array.from({ length: nodeCount }, () => ({
      x: Math.random() * canvasEl.width,
      y: Math.random() * canvasEl.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      r: Math.random() * 2 + 1.5,
      phase: Math.random() * Math.PI * 2,
    }));

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, nodes);
    }

    window.addEventListener("resize", onResize);

    let frame = 0;

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);
      frame += frameSteps;

      for (let indexA = 0; indexA < nodes.length; indexA++) {
        for (let indexB = indexA + 1; indexB < nodes.length; indexB++) {
          const nodeA = nodes[indexA];
          const nodeB = nodes[indexB];
          const dx = nodeB.x - nodeA.x;
          const dy = nodeB.y - nodeA.y;
          const distance = Math.hypot(dx, dy);
          if (distance < MAX_DIST) {
            const lineAlpha = (1 - distance / MAX_DIST) ** 1.4 * 0.72;
            context.beginPath();
            context.moveTo(nodeA.x, nodeA.y);
            context.lineTo(nodeB.x, nodeB.y);
            context.strokeStyle = tc(lineAlpha);
            context.lineWidth = 1.1;
            context.stroke();
          }
        }
      }

      for (const node of nodes) {
        const pulse = 0.6 + Math.sin(frame * 0.025 + node.phase) * 0.25;

        const gradient = context.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.r * 5.5);
        gradient.addColorStop(0, tg(pulse * 0.55));
        gradient.addColorStop(1, gradEnd());
        context.beginPath();
        context.arc(node.x, node.y, node.r * 5.5, 0, Math.PI * 2);
        context.fillStyle = gradient;
        context.fill();

        context.beginPath();
        context.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        context.fillStyle = tc(pulse);
        context.fill();

        node.x += node.vx * frameSteps;
        node.y += node.vy * frameSteps;
        if (node.x < 0 || node.x > canvasEl.width) {
          node.vx *= -1;
        }
        if (node.y < 0 || node.y > canvasEl.height) {
          node.vy *= -1;
        }
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};
